import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  AlertTriangle,
  Building2,
  CheckCircle,
  Copy,
  Edit,
  MapPin,
  Plus,
  Save,
  Trash2,
  Users,
} from 'lucide-react'
import { toast } from 'sonner'

import {
  createTenant,
  deleteTenant,
  getTenants,
  updateTenant,
  type Tenant,
  type TenantUpsertPayload,
} from 'shared/api/tenantService'
import { Alert, AlertDescription } from 'shared/ui/alert'
import { Badge } from 'shared/ui/badge'
import { Button } from 'shared/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from 'shared/ui/card'
import { Input } from 'shared/ui/input'
import { Label } from 'shared/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'shared/ui/select'
import { Separator } from 'shared/ui/separator'
import { Switch } from 'shared/ui/switch'
import { Textarea } from 'shared/ui/textarea'

interface SiteConfigurationProps {
  currentUser?: {
    name?: string | null
  }
}

interface SiteFormState {
  tenantId?: string
  name: string
  description: string
  location: string
  statusId: string
  maxUser: number | null
  isEpermitSystem: boolean
  isReportingSystem: boolean
  isAuditTrail: boolean
  isMultiLanguage: boolean
  isAutoApprove: boolean
  isEmailNotifications: boolean
  workflowLevelId: string
  backupFrequencyId: string
  isActive: boolean
}

const STATUS_OPTIONS: Array<{ value: string; label: string; tone: 'success' | 'warning' | 'secondary' }> = [
  { value: 'AC', label: 'Active', tone: 'success' },
  { value: 'MA', label: 'Maintenance', tone: 'warning' },
  { value: 'IN', label: 'Inactive', tone: 'secondary' },
]

const WORKFLOW_OPTIONS = [
  { value: 'L1', label: '1 Level (Basic)' },
  { value: 'L2', label: '2 Levels (Standard)' },
  { value: 'L3', label: '3 Levels (Advanced)' },
]

const BACKUP_OPTIONS = [
  { value: 'DA', label: 'Daily' },
  { value: 'WE', label: 'Weekly' },
  { value: 'MO', label: 'Monthly' },
]

const normalizeStatusId = (statusId: string | null | undefined, isActive: boolean): string => {
  if (!statusId) {
    return isActive ? 'AC' : 'IN'
  }
  const normalized = statusId.toUpperCase()
  if (STATUS_OPTIONS.some((option) => option.value === normalized)) {
    return normalized
  }
  const legacyMapping: Record<string, string> = {
    ACTIVE: 'AC',
    ACT: 'AC',
    A: 'AC',
    MAINTENANCE: 'MA',
    MAINT: 'MA',
    M: 'MA',
    INACTIVE: 'IN',
    INACT: 'IN',
    INAC: 'IN',
    INACTIVATE: 'IN',
    INACTIVATED: 'IN',
    INACTIVES: 'IN',
    IN: 'IN',
    I: 'IN',
  }
  return legacyMapping[normalized] ?? (isActive ? 'AC' : 'IN')
}

const normalizeWorkflowId = (workflowId: string | null | undefined): string => {
  if (!workflowId) {
    return 'L1'
  }
  const normalized = workflowId.toUpperCase()
  if (WORKFLOW_OPTIONS.some((option) => option.value === normalized)) {
    return normalized
  }
  const legacyMapping: Record<string, string> = {
    '1': 'L1',
    '2': 'L2',
    '3': 'L3',
  }
  return legacyMapping[normalized] ?? 'L1'
}

const normalizeBackupId = (backupId: string | null | undefined): string => {
  if (!backupId) {
    return 'DA'
  }
  const normalized = backupId.toUpperCase()
  if (BACKUP_OPTIONS.some((option) => option.value === normalized)) {
    return normalized
  }
  const legacyMapping: Record<string, string> = {
    DAILY: 'DA',
    WEEKLY: 'WE',
    MONTHLY: 'MO',
    D: 'DA',
    W: 'WE',
    M: 'MO',
  }
  return legacyMapping[normalized] ?? 'DA'
}

const createEmptyFormState = (): SiteFormState => ({
  tenantId: undefined,
  name: '',
  description: '',
  location: '',
  statusId: 'AC',
  maxUser: null,
  isEpermitSystem: true,
  isReportingSystem: false,
  isAuditTrail: false,
  isMultiLanguage: false,
  isAutoApprove: false,
  isEmailNotifications: false,
  workflowLevelId: 'L1',
  backupFrequencyId: 'DA',
  isActive: true,
})

const mapTenantToFormState = (tenant: Tenant): SiteFormState => ({
  tenantId: tenant.tenantId,
  name: tenant.name,
  description: tenant.description ?? '',
  location: tenant.location ?? '',
  statusId: normalizeStatusId(tenant.statusId, tenant.isActive),
  maxUser: tenant.maxUser ?? null,
  isEpermitSystem: tenant.isEpermitSystem,
  isReportingSystem: tenant.isReportingSystem,
  isAuditTrail: tenant.isAuditTrail,
  isMultiLanguage: tenant.isMultiLanguage,
  isAutoApprove: tenant.isAutoApprove,
  isEmailNotifications: tenant.isEmailNotifications,
  workflowLevelId: normalizeWorkflowId(tenant.workflowLevelId),
  backupFrequencyId: normalizeBackupId(tenant.backupFrequencyId),
  isActive: tenant.isActive,
})

const toUpsertPayload = (form: SiteFormState): TenantUpsertPayload => ({
  name: form.name.trim(),
  description: form.description.trim() || null,
  location: form.location.trim() || null,
  statusId: form.statusId ? normalizeStatusId(form.statusId, form.isActive) : null,
  maxUser: form.maxUser != null ? form.maxUser : null,
  isEpermitSystem: form.isEpermitSystem,
  isReportingSystem: form.isReportingSystem,
  isAuditTrail: form.isAuditTrail,
  isMultiLanguage: form.isMultiLanguage,
  isAutoApprove: form.isAutoApprove,
  isEmailNotifications: form.isEmailNotifications,
  workflowLevelId: form.workflowLevelId ? form.workflowLevelId.toUpperCase() : null,
  backupFrequencyId: form.backupFrequencyId ? form.backupFrequencyId.toUpperCase() : null,
  isActive: form.isActive,
})

type DesignerMode = 'create' | 'edit'

interface SiteListViewProps {
  sites: Tenant[]
  isLoading: boolean
  errorMessage: string | null
  lastSyncedAt: Date | null
  onCreate: () => void
  onEdit: (tenant: Tenant) => void
  onCopy: (tenant: Tenant) => void
  onDelete: (tenant: Tenant) => void
}

const SiteListView = ({
  sites,
  isLoading,
  errorMessage,
  lastSyncedAt,
  onCreate,
  onEdit,
  onCopy,
  onDelete,
}: SiteListViewProps) => {
  const totalSites = sites.length
  const activeSites = sites.filter((site) => site.isActive).length
  const maintenanceSites = sites.filter((site) => normalizeStatusId(site.statusId, site.isActive) === 'MA').length
  const totalActiveUsers = sites.reduce((total, site) => total + site.activeUsers, 0)

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Site Configuration</h1>
          <p className="text-sm text-gray-500">จัดการรายการไซต์ทั้งหมด ก่อนเข้าสู่หน้า Site Designer</p>
        </div>
        <Button className="inline-flex items-center gap-2" onClick={onCreate}>
          <Plus className="h-4 w-4" />
          New Site
        </Button>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <Card className="shadow-sm">
          <CardContent className="flex items-center justify-between p-4">
            <div>
              <p className="text-sm text-gray-500">จำนวนไซต์ทั้งหมด</p>
              <p className="text-2xl font-semibold text-gray-900">{totalSites.toLocaleString('th-TH')}</p>
            </div>
            <Building2 className="h-8 w-8 text-blue-500" />
          </CardContent>
        </Card>
        <Card className="shadow-sm">
          <CardContent className="flex items-center justify-between p-4">
            <div>
              <p className="text-sm text-gray-500">ไซต์ที่ Active</p>
              <p className="text-2xl font-semibold text-emerald-600">{activeSites.toLocaleString('th-TH')}</p>
            </div>
            <CheckCircle className="h-8 w-8 text-emerald-500" />
          </CardContent>
        </Card>
        <Card className="shadow-sm">
          <CardContent className="flex items-center justify-between p-4">
            <div>
              <p className="text-sm text-gray-500">ไซต์ในสถานะ Maintenance</p>
              <p className="text-2xl font-semibold text-amber-600">{maintenanceSites.toLocaleString('th-TH')}</p>
            </div>
            <AlertTriangle className="h-8 w-8 text-amber-500" />
          </CardContent>
        </Card>
        <Card className="shadow-sm">
          <CardContent className="flex items-center justify-between p-4">
            <div>
              <p className="text-sm text-gray-500">ผู้ใช้งานที่ใช้งานอยู่</p>
              <p className="text-2xl font-semibold text-indigo-600">{totalActiveUsers.toLocaleString('th-TH')}</p>
            </div>
            <Users className="h-8 w-8 text-indigo-500" />
          </CardContent>
        </Card>
      </div>

      {errorMessage ? (
        <Alert variant="destructive">
          <AlertDescription>{errorMessage}</AlertDescription>
        </Alert>
      ) : null}

      <Card className="shadow-sm">
        <CardHeader className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <CardTitle className="text-base font-semibold text-gray-900">รายการไซต์ทั้งหมด</CardTitle>
            <CardDescription className="text-sm text-gray-600">
              แสดงรายละเอียดโดยสังเขปของแต่ละไซต์ พร้อมจำนวนผู้ใช้งานและสถานะ
            </CardDescription>
          </div>
          {lastSyncedAt ? (
            <p className="text-xs text-gray-500">อัปเดตล่าสุด {lastSyncedAt.toLocaleString('th-TH')}</p>
          ) : null}
        </CardHeader>
        <CardContent className="space-y-4">
          {isLoading ? (
            <div className="space-y-3">
              {Array.from({ length: 3 }).map((_, index) => (
                <div key={index} className="h-20 animate-pulse rounded-md border border-dashed border-gray-200 bg-gray-50" />
              ))}
            </div>
          ) : sites.length === 0 ? (
            <Alert>
              <AlertDescription>ยังไม่มีข้อมูลไซต์ในระบบ</AlertDescription>
            </Alert>
          ) : (
            <div className="space-y-4">
              {sites.map((site) => {
                const statusLabel = STATUS_OPTIONS.find(
                  (option) => option.value === normalizeStatusId(site.statusId, site.isActive),
                )?.label
                const statusTone = STATUS_OPTIONS.find(
                  (option) => option.value === normalizeStatusId(site.statusId, site.isActive),
                )?.tone

                return (
                  <div key={site.tenantId} className="rounded-md border border-gray-200 bg-white p-4 shadow-sm">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <div className="space-y-2">
                        <div className="flex flex-wrap items-center gap-2">
                          <Building2 className="h-4 w-4 text-orange-500" />
                          <p className="text-base font-semibold text-gray-900">{site.name}</p>
                          {statusLabel ? (
                            <Badge
                              variant={
                                statusTone === 'success' ? 'default' : statusTone === 'warning' ? 'destructive' : 'secondary'
                              }
                            >
                              {statusLabel}
                            </Badge>
                          ) : null}
                          {site.maxUser ? (
                            <Badge variant="outline" className="text-xs text-gray-600">
                              Max {site.maxUser.toLocaleString('th-TH')} users
                            </Badge>
                          ) : null}
                        </div>
                        <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600">
                          <span className="inline-flex items-center gap-1">
                            <Users className="h-3.5 w-3.5 text-indigo-500" />
                            {site.activeUsers.toLocaleString('th-TH')} คน
                          </span>
                          {site.location ? (
                            <span className="inline-flex items-center gap-1">
                              <MapPin className="h-3.5 w-3.5 text-rose-500" />
                              {site.location}
                            </span>
                          ) : null}
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Button type="button" variant="outline" size="sm" onClick={() => onEdit(site)}>
                          <Edit className="mr-1 h-4 w-4" />
                          Edit
                        </Button>
                        <Button type="button" variant="outline" size="sm" onClick={() => onCopy(site)}>
                          <Copy className="mr-1 h-4 w-4" />
                          Copy
                        </Button>
                        <Button type="button" variant="destructive" size="sm" onClick={() => onDelete(site)}>
                          <Trash2 className="mr-1 h-4 w-4" />
                          Delete
                        </Button>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

interface SiteDesignerViewProps {
  mode: DesignerMode
  form: SiteFormState
  onChange: (form: SiteFormState) => void
  onSubmit: () => void
  onCancel: () => void
  isSubmitting: boolean
}

const SiteDesignerView = ({ mode, form, onChange, onSubmit, onCancel, isSubmitting }: SiteDesignerViewProps) => {
  const handleFieldChange = <K extends keyof SiteFormState>(key: K, value: SiteFormState[K]) => {
    onChange({ ...form, [key]: value })
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
          <Button variant="link" className="px-0 text-blue-600" onClick={onCancel}>
            ← กลับไปหน้า Site List
          </Button>
        <Button onClick={onSubmit} disabled={isSubmitting} className="inline-flex items-center gap-2">
          <Save className="h-4 w-4" />
          {isSubmitting ? 'กำลังบันทึก...' : mode === 'create' ? 'สร้าง Site' : 'บันทึกการเปลี่ยนแปลง'}
        </Button>
      </div>
      <Card className="border border-gray-200 shadow-sm">
        <CardHeader>
          <CardTitle className="text-base font-semibold text-gray-900">
            {mode === 'create' ? 'สร้างไซต์ใหม่' : `แก้ไขข้อมูลไซต์: ${form.name}`}
          </CardTitle>
          <CardDescription className="text-sm text-gray-500">
            กำหนดข้อมูลทั่วไปและคุณสมบัติของไซต์
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 py-2 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="site-name">ชื่อไซต์ *</Label>
              <Input
                id="site-name"
                value={form.name}
                onChange={(event) => handleFieldChange('name', event.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="site-location">สถานที่ตั้ง</Label>
              <Input
                id="site-location"
                value={form.location}
                onChange={(event) => handleFieldChange('location', event.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="site-status">สถานะ</Label>
              <Select value={form.statusId} onValueChange={(value) => handleFieldChange('statusId', value)}>
                <SelectTrigger id="site-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="site-max-user">จำนวนผู้ใช้สูงสุด</Label>
              <Input
                id="site-max-user"
                type="number"
                min={0}
                value={form.maxUser ?? ''}
                onChange={(event) => {
                  const value = event.target.value
                  handleFieldChange('maxUser', value === '' ? null : Math.max(0, Number(value)))
                }}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="site-workflow">Workflow Level</Label>
              <Select value={form.workflowLevelId} onValueChange={(value) => handleFieldChange('workflowLevelId', value)}>
                <SelectTrigger id="site-workflow">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {WORKFLOW_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="site-backup">ความถี่การสำรองข้อมูล</Label>
              <Select
                value={form.backupFrequencyId}
                onValueChange={(value) => handleFieldChange('backupFrequencyId', value)}
              >
                <SelectTrigger id="site-backup">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {BACKUP_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="site-description">รายละเอียดเพิ่มเติม</Label>
            <Textarea
              id="site-description"
              value={form.description}
              onChange={(event) => handleFieldChange('description', event.target.value)}
              rows={3}
            />
          </div>

          <Separator />

          <div className="grid gap-3 sm:grid-cols-2">
            {[
              {
                label: 'E-Permit',
                description: 'เปิดใช้งานระบบ e-Permit',
                key: 'isEpermitSystem' as const,
              },
              {
                label: 'Reporting',
                description: 'รายงานสถิติและแดชบอร์ด',
                key: 'isReportingSystem' as const,
              },
              {
                label: 'Audit Trail',
                description: 'บันทึกกิจกรรมสำคัญของผู้ใช้',
                key: 'isAuditTrail' as const,
              },
              {
                label: 'Multi-language',
                description: 'รองรับหลายภาษา',
                key: 'isMultiLanguage' as const,
              },
              {
                label: 'Auto Approval',
                description: 'อนุมัติอัตโนมัติในเงื่อนไขที่กำหนด',
                key: 'isAutoApprove' as const,
              },
              {
                label: 'Email Notification',
                description: 'แจ้งเตือนผ่านอีเมล',
                key: 'isEmailNotifications' as const,
              },
            ].map((item) => (
              <div key={item.label} className="flex items-center justify-between rounded-md border border-dashed border-gray-200 p-3">
                <div>
                  <p className="text-sm font-medium text-gray-800">{item.label}</p>
                  <p className="text-xs text-gray-500">{item.description}</p>
                </div>
                <Switch
                  checked={Boolean(form[item.key])}
                  onCheckedChange={(checked) => handleFieldChange(item.key, checked as SiteFormState[typeof item.key])}
                />
              </div>
            ))}
            <div className="flex items-center justify-between rounded-md border border-dashed border-gray-200 p-3 sm:col-span-2">
              <div>
                <p className="text-sm font-medium text-gray-800">สถานะการใช้งาน</p>
                <p className="text-xs text-gray-500">เปิดหรือปิดการใช้งานไซต์</p>
              </div>
              <Switch checked={form.isActive} onCheckedChange={(checked) => handleFieldChange('isActive', checked)} />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

export function SiteConfiguration({ currentUser }: SiteConfigurationProps) {
  const [sites, setSites] = useState<Tenant[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [lastSyncedAt, setLastSyncedAt] = useState<Date | null>(null)

  const [viewMode, setViewMode] = useState<'list' | 'designer'>('list')
  const [designerMode, setDesignerMode] = useState<DesignerMode>('create')
  const [designerForm, setDesignerForm] = useState<SiteFormState>(createEmptyFormState())
  const [isSubmitting, setIsSubmitting] = useState(false)

  const loadSites = useCallback(async () => {
    setIsLoading(true)
    setErrorMessage(null)
    try {
      const tenantList = await getTenants()
      const sorted = tenantList.sort((a, b) => a.name.localeCompare(b.name, 'th-TH'))
      setSites(sorted)
      setLastSyncedAt(new Date())
    } catch (error) {
      console.error('SiteConfiguration: failed to load tenants', error)
      setErrorMessage('ไม่สามารถโหลดข้อมูลไซต์ได้')
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    loadSites()
  }, [loadSites])

  const upsertSiteLocally = useCallback((tenant: Tenant) => {
    setSites((prev) => {
      const filtered = prev.filter((item) => item.tenantId !== tenant.tenantId)
      const next = [...filtered, tenant].sort((a, b) => a.name.localeCompare(b.name, 'th-TH'))
      return next
    })
  }, [])

  const handleCreate = () => {
    setDesignerForm(createEmptyFormState())
    setDesignerMode('create')
    setViewMode('designer')
  }

  const handleEdit = (tenant: Tenant) => {
    setDesignerForm(mapTenantToFormState(tenant))
    setDesignerMode('edit')
    setViewMode('designer')
  }

  const handleCopy = (tenant: Tenant) => {
    const form = mapTenantToFormState(tenant)
    setDesignerForm({
      ...form,
      tenantId: undefined,
      name: `${form.name} (Copy)`,
      workflowLevelId: form.workflowLevelId ?? 'L1',
      backupFrequencyId: form.backupFrequencyId ?? 'DA',
      isActive: true,
    })
    setDesignerMode('create')
    setViewMode('designer')
  }

  const handleDelete = async (tenant: Tenant) => {
    const confirmed = window.confirm(`ยืนยันการลบไซต์ ${tenant.name}?`)
    if (!confirmed) {
      return
    }
    try {
      await deleteTenant(tenant.tenantId)
      setSites((prev) => prev.filter((site) => site.tenantId !== tenant.tenantId))
      toast.success('ลบไซต์เรียบร้อย')
    } catch (error) {
      console.error('SiteConfiguration: delete failed', error)
      toast.error('ไม่สามารถลบไซต์ได้')
    }
  }

  const handleDesignerSubmit = async () => {
    if (!designerForm.name.trim()) {
      toast.error('กรุณากรอกชื่อไซต์')
      return
    }

    setIsSubmitting(true)
    try {
      if (designerMode === 'create') {
        const created = await createTenant(toUpsertPayload(designerForm))
        upsertSiteLocally(created)
        toast.success('สร้างไซต์ใหม่เรียบร้อย')
      } else if (designerMode === 'edit' && designerForm.tenantId) {
        const updated = await updateTenant(designerForm.tenantId, toUpsertPayload(designerForm))
        upsertSiteLocally(updated)
        toast.success('อัปเดตข้อมูลไซต์เรียบร้อย')
      }
      setViewMode('list')
    } catch (error) {
      console.error('SiteConfiguration: save failed', error)
      toast.error('ไม่สามารถบันทึกข้อมูลไซต์ได้')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCancelDesigner = () => {
    setViewMode('list')
    setDesignerForm(createEmptyFormState())
  }

  if (viewMode === 'designer') {
    return (
      <SiteDesignerView
        mode={designerMode}
        form={designerForm}
        onChange={setDesignerForm}
        onSubmit={handleDesignerSubmit}
        onCancel={handleCancelDesigner}
        isSubmitting={isSubmitting}
      />
    )
  }

  return (
    <SiteListView
      sites={sites}
      isLoading={isLoading}
      errorMessage={errorMessage}
      lastSyncedAt={lastSyncedAt}
      onCreate={handleCreate}
      onEdit={handleEdit}
      onCopy={handleCopy}
      onDelete={handleDelete}
    />
  )
}

export default SiteConfiguration
